import { Injectable } from '@angular/core';
import * as QRCode from 'qrcode';

@Injectable({
  providedIn: 'root'
})
export class QrCodeService {

  constructor() { }

  /**
   * Gerar QR Code como URL de Dados (imagem base64)
   */
  async generateQrCode(text: string, options?: QRCode.QRCodeToDataURLOptions): Promise<string> {
    try {
      
      const defaultOptions: QRCode.QRCodeToDataURLOptions = {
        errorCorrectionLevel: 'L', // LOWER para textos mais longos
        type: 'image/png',
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        width: 256
      };

      const qrOptions = { ...defaultOptions, ...options };
      
      const result = await QRCode.toDataURL(text, qrOptions);
      return result;
    } catch (error) {
      console.error('Erro ao gerar QR code:', error);

      // tentar mesmo com correção de erro mínima
      try {
        const fallbackOptions: QRCode.QRCodeToDataURLOptions = {
          errorCorrectionLevel: 'L',
          type: 'image/png',
          margin: 0,
          width: 256
        };
        
        return await QRCode.toDataURL(text, fallbackOptions);
      } catch (fallbackError) {
        console.error('Erro no fallback de geração de QR Code mesmo com configurações mínimas:', fallbackError);
        throw new Error('Erro ao gerar QR code mesmo com configurações mínimas');
      }
    }
  }

  /**
   * Gerar QR Code como string SVG
   */
  async generateQrCodeSvg(text: string, options?: QRCode.QRCodeToStringOptions): Promise<string> {
    try {
      const defaultOptions: QRCode.QRCodeToStringOptions = {
        type: 'svg',
        errorCorrectionLevel: 'M',
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        width: 256
      };

      const qrOptions = { ...defaultOptions, ...options };
      return await QRCode.toString(text, qrOptions);
    } catch (error) {
      console.error('Erro ao gerar QR Code como string SVG:', error);
      throw new Error('Erro ao gerar QR code como string SVG');
    }
  }

  /**
   * Baixar QR Code como arquivo PNG
   */
  async downloadQrCode(text: string, filename: string = 'qrcode.png'): Promise<Blob> {
    try {
      // Validar o tamanho do texto primeiro
      const validation = this.validateTextLength(text);
      if (!validation.isValid) {
        throw new Error(`Texto muito longo para QR Code: ${validation.actualLength} caracteres (máximo: ${validation.maxLength})`);
      }
      const dataUrl = await this.generateQrCode(text, { width: 512 });
      // Converter URL de dados para blob
      const response = await fetch(dataUrl);
      if (!response.ok) {
        throw new Error(`Erro ao converter QR Code: ${response.statusText}`);
      }
      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Erro detalhado ao gerar QR Code:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      throw new Error(`Erro ao gerar QR Code: ${errorMessage}`);
    }
  }

  salvarQrCode(blob: any, filename: string) {
      // Criar link de download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Cleanup
      URL.revokeObjectURL(url);
  }

  /**
   * Checar se o comprimento do texto é adequado para o QR Code
   */
  validateTextLength(text: string): { isValid: boolean; maxLength: number; actualLength: number } {
    const maxLength = 4296; // Máximo de caracteres alfanuméricos para QR Code
    const actualLength = text.length;
    return {
      isValid: actualLength <= maxLength,
      maxLength,
      actualLength
    };
  }
}
